import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { CartPreview } from '@features/cart'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesBranch } from '@widgets/categories-branch'
import ProductsInfiniteScroll from '@widgets/products-infinite-scroll'


import { IMAGE_SIZES, URL_PAGE_CATALOG } from '@shared/contants'
import { Product, ProductsPreviewCardQuery, ProductsPreviewCardQueryVariables, useProductsPreviewCardQuery, useSingleProductGroupQuery } from '@shared/generated'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

const LIMIT = 12

export default function ProductGroupProductsPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { shopId } = useAddressStore()
  const params = useParams()

  const productGroupId = params.productGroupId

  const { data: productGroupData } = useSingleProductGroupQuery({
    variables: {
      id: productGroupId!,
      shop_id: shopId,
      style: IMAGE_SIZES.LARGE,
    },
    skip: !productGroupId,
  })

  const productGroupLabel = productGroupData?.singleProductGroup?.label ?? ''

  const { data, loading, fetchMore } = useProductsPreviewCardQuery({
    variables: {
      shopId: shopId,
      pager: { page: 1, limit: LIMIT },
      filter: {
        fields: [ 'productGroupIds' ],
        op: 'contains',
        value: [ productGroupId ]
      },
    },
    skip: !productGroupId,
  })
  
  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      ProductsPreviewCardQuery,
      ProductsPreviewCardQueryVariables
    >({
      loading: loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        pager: { limit: LIMIT },
        filter: {
          fields: [ 'productGroupIds' ],
          op: 'contains',
          value: [ productGroupId ]
        },
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.products?.edges ?? []
        const curr = fetched?.products?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            products: {
              ...cached.products,
              edges: [ ...prev, ...curr ],
            },
          } as ProductsPreviewCardQuery,
        }
      },
    })

  const totalProducts = data?.products?.pageInfo?.total ?? 0

  return (
    <Container>
      <PageGrid aside={<CartPreview />}>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: URL_PAGE_CATALOG
                },
                {
                  title: productGroupLabel
                }
              ]}
            />
            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>
              </div>
            )}
            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                <TitleWithCount
                  className={styles.root__title}
                  title={productGroupLabel}
                >
                  {totalProducts !== null && (
                    <>
                      {totalProducts}{' '}
                      {getNoun(
                        totalProducts as number,
                        tp('product.1'),
                        tp('product.2'),
                        tp('product.3'),
                      )}
                    </>
                  )}
                </TitleWithCount>
              </div>
              <ProductsInfiniteScroll
                products={data?.products?.edges as Product[]}
                loading={loading}
                fetchNextPage={fetchNextPage}
                hasMore={hasMore}
                loadingMore={loadingMore}
              />
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}